import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Database } from '@/integrations/supabase/types';

type AvaliacaoGUT = Database['public']['Tables']['avaliacao_gut']['Row'];
type AvaliacaoGUTInsert = Database['public']['Tables']['avaliacao_gut']['Insert'];

// Helpers para classificação GUT
export const calcularScoreGUT = (gravidade: number, urgencia: number, tendencia: number): number => {
  return gravidade * urgencia * tendencia;
};

export const getGUTPrioridade = (score: number): string => {
  if (score >= 64) return 'critica';
  if (score >= 27) return 'alta';
  if (score >= 8) return 'media';
  return 'baixa';
};

export const GUT_LABELS = {
  gravidade: {
    1: 'Sem gravidade',
    2: 'Pouco grave',
    3: 'Grave',
    4: 'Muito grave',
    5: 'Extremamente grave'
  },
  urgencia: {
    1: 'Pode esperar',
    2: 'Pouco urgente',
    3: 'Urgente',
    4: 'Muito urgente',
    5: 'Ação imediata'
  },
  tendencia: {
    1: 'Não piora',
    2: 'Piora a longo prazo',
    3: 'Piora a médio prazo',
    4: 'Piora a curto prazo',
    5: 'Piora rapidamente'
  }
};

export function useAvaliacaoGUT(conformidadeId: string | undefined) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['avaliacao-gut', conformidadeId],
    queryFn: async () => {
      if (!conformidadeId) return null;
      const { data, error } = await supabase
        .from('avaliacao_gut')
        .select(`
          *,
          avaliador:users!avaliacao_gut_avaliado_por_fkey(id, name)
        `)
        .eq('conformidade_id', conformidadeId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!conformidadeId
  });

  const upsertMutation = useMutation({
    mutationFn: async (data: AvaliacaoGUTInsert) => {
      // Calcular score e prioridade automaticamente
      const score = calcularScoreGUT(
        data.gravidade ?? 1,
        data.urgencia ?? 1,
        data.tendencia ?? 1
      );
      const prioridade = getGUTPrioridade(score);

      const payload: AvaliacaoGUTInsert = {
        ...data,
        score_gut: score,
        prioridade
      };

      const { data: result, error } = await supabase
        .from('avaliacao_gut')
        .upsert(payload)
        .select()
        .single();
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avaliacao-gut', conformidadeId] });
      queryClient.invalidateQueries({ queryKey: ['conformidades'] });
      toast({ title: 'Avaliação GUT salva com sucesso' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao salvar avaliação GUT', description: error.message, variant: 'destructive' });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('avaliacao_gut').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['avaliacao-gut', conformidadeId] });
      toast({ title: 'Avaliação GUT excluída' });
    },
    onError: (error: Error) => {
      toast({ title: 'Erro ao excluir avaliação', description: error.message, variant: 'destructive' });
    }
  });

  return {
    avaliacao: query.data,
    isLoading: query.isLoading,
    error: query.error,
    saveAvaliacao: upsertMutation.mutate,
    deleteAvaliacao: deleteMutation.mutate,
    isSaving: upsertMutation.isPending,
    isDeleting: deleteMutation.isPending
  };
}
